"use client";

/**
 * `<Boardroom>` — the 12-seat AI investor debate table for /simulate.
 *
 * Reimplemented from `docs/Fable UiUx review/simulate-boardroom-prototype.
 * html` on the committed motion system. Seats light up as their agent
 * speaks (gold core + expanding ripple), dual argument arcs fire across
 * the table with traveling pulse dots, per-seat conviction rings fill and
 * colorize as the debate matures, a bull/bear sentiment gauge tracks the
 * room, a persona-tagged transcript scrolls in, a bias monitor surfaces
 * late-debate findings, and the loop resolves into a consensus card.
 *
 * Architecture (Global rule 5): the whole animation is the pure function
 * `boardroomStateAt(frame)` over the `useStageDriver` clock — three
 * stages: CONVENING → DEBATING (15 rounds) → VERDICT. No setTimeout
 * timeline anywhere. The two ripples and the two arc pulse dots paint
 * imperatively via the driver's `onFrame` escape hatch (they move every
 * frame); everything else re-renders declaratively at ~12fps with CSS
 * transitions smoothing between states. Hover pauses, off-viewport
 * pauses, and looping all come from the driver.
 *
 * Reduced motion + pre-hydration: renders the settled VERDICT state —
 * full conviction rings, final sentiment, complete transcript, bias note,
 * consensus card — statically.
 *
 * COMPLIANCE NOTE: every transcript line is ILLUSTRATIVE. The agents are
 * AI personas that may voice strong opinions; the platform endorses none
 * of them. The component renders that framing as a visible disclaimer.
 */

import { useRef, useEffect, useState, type CSSProperties } from "react";
import { useStageDriver, frameAt, type StageFrame } from "@/lib/motion";
import { EASE, LINE, LINE_STRONG, MONO, SERIF } from "./shared";

/* ----------------------------- panel data ----------------------------- */

interface Seat {
  x: number;
  y: number;
  init: string;
  label: string;
  lx: number;
  ly: number;
  anchor: "start" | "middle" | "end";
}

/** 12 seats around the oval (viewBox 460×260), from the prototype. */
const SEATS: Seat[] = [
  { x: 230, y: 45, init: "QU", label: "QUALITY", lx: 0, ly: -21, anchor: "middle" },
  { x: 315, y: 56, init: "MO", label: "MOMENTUM", lx: 14, ly: -14, anchor: "start" },
  { x: 377, y: 87, init: "MA", label: "MACRO", lx: 20, ly: 0, anchor: "start" },
  { x: 400, y: 130, init: "SH", label: "SHORT", lx: 22, ly: 3, anchor: "start" },
  { x: 377, y: 172, init: "QT", label: "QUANT", lx: 20, ly: 8, anchor: "start" },
  { x: 315, y: 204, init: "IN", label: "INCOME", lx: 14, ly: 20, anchor: "start" },
  { x: 230, y: 215, init: "VA", label: "VALUE", lx: 0, ly: 34, anchor: "middle" },
  { x: 145, y: 204, init: "CO", label: "CONTRARIAN", lx: -14, ly: 20, anchor: "end" },
  { x: 82, y: 172, init: "GR", label: "GROWTH", lx: -20, ly: 8, anchor: "end" },
  { x: 60, y: 130, init: "AC", label: "ACTIVIST", lx: -22, ly: 3, anchor: "end" },
  { x: 82, y: 87, init: "DV", label: "DIVIDEND", lx: -20, ly: 0, anchor: "end" },
  { x: 145, y: 56, init: "GP", label: "GARP", lx: -14, ly: -14, anchor: "end" },
];

/** Argument arcs (quadratic Béziers) between opposing seats. */
const ARCS = [
  { p0: [230, 45], c: [260, 130], p2: [230, 215] },
  { p0: [400, 130], c: [230, 90], p2: [60, 130] },
  { p0: [315, 56], c: [230, 130], p2: [145, 204] },
  { p0: [82, 87], c: [230, 130], p2: [377, 172] },
  { p0: [145, 56], c: [240, 115], p2: [400, 130] },
  { p0: [377, 87], c: [230, 150], p2: [82, 172] },
] as const;

function arcPath(a: (typeof ARCS)[number]): string {
  return `M${a.p0[0]},${a.p0[1]} Q${a.c[0]},${a.c[1]} ${a.p2[0]},${a.p2[1]}`;
}

/** Point on a quadratic Bézier at parameter t. */
function quadPoint(a: (typeof ARCS)[number], t: number): readonly [number, number] {
  const u = 1 - t;
  return [
    u * u * a.p0[0] + 2 * u * t * a.c[0] + t * t * a.p2[0],
    u * u * a.p0[1] + 2 * u * t * a.c[1] + t * t * a.p2[1],
  ];
}

const ROUNDS = 15;
/** Final per-seat conviction (0–10) the rings settle to. */
const CONV_TARGET = [8.1, 7.4, 3.8, 2.6, 6.4, 5.5, 3.9, 5.2, 7.8, 6.8, 5.0, 7.1];
/** Bull% of the room per round. */
const SENTIMENT = [50, 48, 52, 55, 53, 57, 55, 59, 57, 60, 62, 61, 63, 64, 64];
/** Which two seats speak each round. */
const SPEAK_A = [6, 1, 7, 0, 3, 10, 2, 8, 5, 11, 4, 9, 6, 0, 7];
const SPEAK_B = [3, 6, 0, 7, 8, 2, 9, 1, 11, 5, 10, 4, 3, 7, 1];

const RING_R = 15;
const RING_CIRC = 2 * Math.PI * RING_R;

function convColor(v: number): string {
  if (v >= 6) return "var(--color-green, #10B981)";
  if (v <= 4.5) return "var(--color-red, #EF4444)";
  return "var(--color-gold, #F59E0B)";
}

type Persona = "bull" | "bear" | "value" | "contrarian";

/** ILLUSTRATIVE transcript — synthetic opinions, endorsed by no one. */
const TRANSCRIPT: Array<{ tag: string; persona: Persona; text: string }> = [
  { tag: "VALUE-07", persona: "value", text: "at 32x forward earnings the entry math does not work" },
  { tag: "MOMENTUM-12", persona: "bull", text: "breadth improving; leaders making higher highs" },
  { tag: "CONTRARIAN-03", persona: "contrarian", text: "everyone in this room owns it; that is the risk" },
  { tag: "QUALITY-01", persona: "bull", text: "ROIC above 25% for eight straight years" },
  { tag: "SHORT-09", persona: "bear", text: "channel checks show inventory building in two segments" },
  { tag: "MACRO-05", persona: "bear", text: "real rates here compress every long-duration name" },
];

const PERSONA_STYLE: Record<Persona, { bg: string; color: string }> = {
  bull: { bg: "rgba(16,185,129,.15)", color: "#34D399" },
  bear: { bg: "rgba(239,68,68,.15)", color: "#F87171" },
  value: { bg: "rgba(59,130,246,.15)", color: "#60A5FA" },
  contrarian: { bg: "rgba(245,158,11,.15)", color: "#FBBF24" },
};

/* --------------------------- pure stage math --------------------------- */

/** Stage durations (ms): CONVENING → DEBATING (15 rounds) → VERDICT. */
const DURATIONS = [1100, 14250, 6500];
const FINAL_FRAME = frameAt(1, DURATIONS);

export interface BoardroomVisualState {
  phase: "CONVENING" | "DEBATING" | "VERDICT";
  /** Displayed round, 0..15. */
  round: number;
  /** 0..1 within the current round (drives ripples + arc dots). */
  roundLocal: number;
  /** The two seats speaking this round, or null outside the debate. */
  speaking: readonly [number, number] | null;
  /** 0..1 fill of the conviction rings. */
  convFraction: number;
  /** Rings colorize (green/gold/red) once the debate matures. */
  colorized: boolean;
  sentimentBull: number;
  /** How many transcript lines have appeared. */
  transcriptShown: number;
  /** Index of the highlighted "live" line, if any. */
  liveLine: number | null;
  biasShown: boolean;
  consensusShown: boolean;
  /** Active argument arcs with the pulse-dot parameter t (0..1). */
  arcs: ReadonlyArray<{ index: number; t: number }>;
}

/**
 * THE pure stage function — the entire boardroom render derives from this.
 * Exported so tests / a future scrub driver can evaluate any instant.
 */
export function boardroomStateAt(frame: StageFrame): BoardroomVisualState {
  if (frame.stage === 0) {
    return {
      phase: "CONVENING",
      round: 0,
      roundLocal: 0,
      speaking: null,
      convFraction: 0,
      colorized: false,
      sentimentBull: 50,
      transcriptShown: 0,
      liveLine: null,
      biasShown: false,
      consensusShown: false,
      arcs: [],
    };
  }
  if (frame.stage === 1) {
    const roundFloat = frame.stageProgress * ROUNDS;
    const idx = Math.min(ROUNDS - 1, Math.floor(roundFloat));
    const roundLocal = Math.min(1, roundFloat - idx);
    const arcs: Array<{ index: number; t: number }> = [];
    const tA = roundLocal / 0.74;
    if (tA <= 1) arcs.push({ index: (idx + 1) % 6, t: tA });
    const tB = (roundLocal - 0.27) / 0.74;
    if (tB >= 0 && tB <= 1) arcs.push({ index: ((idx + 1) * 2 + 1) % 6, t: tB });
    // one transcript line roughly every 2.2 rounds, starting round ~1.3
    const shown = Math.max(
      0,
      Math.min(TRANSCRIPT.length, Math.floor((roundFloat - 1.3) / 2.2) + 1)
    );
    return {
      phase: "DEBATING",
      round: idx + 1,
      roundLocal,
      speaking: [SPEAK_A[idx], SPEAK_B[idx]],
      convFraction: (idx + 1) / ROUNDS,
      colorized: idx + 1 > 7,
      sentimentBull: SENTIMENT[idx],
      transcriptShown: shown,
      liveLine: shown > 0 ? shown - 1 : null,
      biasShown: roundFloat >= 13.6,
      consensusShown: false,
      arcs,
    };
  }
  // VERDICT — also the settled reduced-motion / pre-hydration state.
  return {
    phase: "VERDICT",
    round: ROUNDS,
    roundLocal: 0,
    speaking: null,
    convFraction: 1,
    colorized: true,
    sentimentBull: SENTIMENT[ROUNDS - 1],
    transcriptShown: TRANSCRIPT.length,
    liveLine: null,
    biasShown: true,
    consensusShown: true,
    arcs: [],
  };
}

/* ------------------------------ component ------------------------------ */

export interface BoardroomConsensus {
  /** Headline verdict, e.g. "64% constructive, split on price". */
  headline: string;
  /** The key disagreement, e.g. "entry valuation". */
  disagreement: string;
  /** The flagged bias, e.g. "herding (9 agents cited the same source)". */
  biasNote: string;
  /** Final scenario split — should sum to 100. */
  scenarios: { bull: number; base: number; bear: number };
}

const DEFAULT_CONSENSUS: BoardroomConsensus = {
  headline: "64% constructive, split on price",
  disagreement: "entry valuation",
  biasNote: "herding (9 agents cited the same source)",
  scenarios: { bull: 30, base: 45, bear: 25 },
};

export interface BoardroomProps {
  /** Ticker shown in the header badge. Default "MSFT". */
  ticker?: string;
  /** Panel size shown in the header badge. Default 80. */
  agentCount?: number;
  /** Verdict content for the consensus card. */
  consensus?: BoardroomConsensus;
  /** Bias-monitor line shown late in the debate. */
  biasMonitor?: string;
  className?: string;
}

const CSS = `
.wtm-brd-grid{display:grid;grid-template-columns:1.25fr 1fr;gap:24px;align-items:start}
@media (max-width:820px){.wtm-brd-grid{grid-template-columns:1fr}}
@keyframes wtm-brd-pulse{0%,100%{opacity:1}50%{opacity:.35}}
@media (prefers-reduced-motion:reduce){.wtm-brd-pulse-dot{animation:none!important}}
`;

const badgeStyle: CSSProperties = {
  border: `1px solid ${LINE_STRONG}`,
  borderRadius: 99,
  padding: "3px 11px",
  color: "var(--color-muted, #94A3B8)",
  whiteSpace: "nowrap",
};

export function Boardroom({
  ticker = "MSFT",
  agentCount = 80,
  consensus = DEFAULT_CONSENSUS,
  biasMonitor = "anchoring detected in 3 agents · herding flagged round 11",
  className,
}: BoardroomProps) {
  // Pre-hydration renders the settled VERDICT; the loop starts on mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // --- imperative paint targets (move every frame)
  const rippleRefs = useRef<Array<SVGCircleElement | null>>([]);
  const pulseRefs = useRef<Array<SVGCircleElement | null>>([]);
  const animatedRef = useRef(false);

  const driver = useStageDriver<HTMLDivElement>({
    durations: DURATIONS,
    reactStateFps: 12, // declarative UI; CSS transitions smooth between states
    onFrame: (frame) => {
      if (!animatedRef.current) return;
      const s = boardroomStateAt(frame);
      // speaking-seat ripples
      for (let k = 0; k < 2; k++) {
        const el = rippleRefs.current[k];
        if (!el) continue;
        if (s.speaking) {
          const seat = SEATS[s.speaking[k]];
          const ph = s.roundLocal;
          el.setAttribute("cx", String(seat.x));
          el.setAttribute("cy", String(seat.y));
          el.setAttribute("r", String(12 + 12 * ph));
          el.setAttribute("opacity", String(0.7 * (1 - ph)));
        } else {
          el.setAttribute("opacity", "0");
        }
      }
      // argument-arc pulse dots
      for (let k = 0; k < 2; k++) {
        const el = pulseRefs.current[k];
        if (!el) continue;
        const arc = s.arcs[k];
        if (arc) {
          const [x, y] = quadPoint(ARCS[arc.index], arc.t);
          el.setAttribute("cx", String(x));
          el.setAttribute("cy", String(y));
          el.setAttribute("opacity", "1");
        } else {
          el.setAttribute("opacity", "0");
        }
      }
    },
  });

  const animated = mounted && !driver.reducedMotion;
  animatedRef.current = animated;

  const state = boardroomStateAt(animated ? driver.frame : FINAL_FRAME);
  const liveText =
    state.phase === "CONVENING" ? "CONVENING" : state.phase === "DEBATING" ? "DEBATE LIVE" : "VERDICT";

  const scenarios = [
    { label: "BULL", pct: consensus.scenarios.bull, fill: "var(--color-green, #10B981)" },
    { label: "BASE", pct: consensus.scenarios.base, fill: "var(--color-blue, #3B82F6)" },
    { label: "BEAR", pct: consensus.scenarios.bear, fill: "var(--color-red, #EF4444)" },
  ];

  return (
    <section
      ref={driver.ref}
      className={className}
      aria-label={`AI investor panel debating ${ticker}, experimental simulation, illustrative transcript`}
      style={{
        background: "var(--color-bg, #0A0E1A)",
        border: `1px solid ${LINE}`,
        borderRadius: 16,
        padding: 22,
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 24px 60px -24px rgba(0,0,0,.6)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <style>{CSS}</style>
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(55% 80% at 32% 45%, rgba(59,130,246,.07), transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* header badges */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          borderBottom: `1px solid ${LINE}`,
          paddingBottom: 14,
          marginBottom: 18,
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: "0.1em",
          color: "var(--color-dim, #7E8FA6)",
          position: "relative",
        }}
      >
        <span
          style={{
            ...badgeStyle,
            border: "1px solid rgba(245,158,11,.5)",
            color: "var(--color-gold, #F59E0B)",
          }}
        >
          WTM SIMULATE
        </span>
        <span style={badgeStyle}>PANEL · {agentCount} AGENTS</span>
        <span style={badgeStyle}>SHOWING 12 SEATS</span>
        <span style={badgeStyle}>TICKER · {ticker}</span>
        <span
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            color: "var(--color-green, #10B981)",
          }}
        >
          <span
            className="wtm-brd-pulse-dot"
            aria-hidden
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "var(--color-green, #10B981)",
              animation: `wtm-brd-pulse 1.6s ${EASE} infinite`,
            }}
          />
          {liveText}
        </span>
      </div>

      <div className="wtm-brd-grid" style={{ position: "relative" }}>
        {/* ===== the table ===== */}
        <div style={{ position: "relative" }}>
          <svg viewBox="0 0 460 260" style={{ width: "100%", display: "block" }} aria-hidden>
            <defs>
              <radialGradient id="wtm-brd-tbl" cx="50%" cy="40%" r="70%">
                <stop offset="0%" stopColor="rgba(59,130,246,.10)" />
                <stop offset="55%" stopColor="rgba(30,41,59,.55)" />
                <stop offset="100%" stopColor="rgba(10,16,31,.9)" />
              </radialGradient>
            </defs>
            <ellipse
              cx={230}
              cy={130}
              rx={172}
              ry={86}
              fill="url(#wtm-brd-tbl)"
              stroke="rgba(148,163,184,.35)"
              strokeWidth={1}
            />
            <ellipse cx={230} cy={130} rx={150} ry={70} fill="none" stroke="rgba(148,163,184,.09)" />

            {/* argument arcs */}
            <g>
              {ARCS.map((a, i) => {
                const lit = state.arcs.some((arc) => arc.index === i);
                return (
                  <path
                    key={i}
                    d={arcPath(a)}
                    fill="none"
                    strokeWidth={1}
                    stroke={lit ? "rgba(245,158,11,.3)" : "rgba(245,158,11,0)"}
                    style={{ transition: `stroke .3s ${EASE}` }}
                  />
                );
              })}
            </g>

            {/* round counter */}
            <text
              x={230}
              y={126}
              textAnchor="middle"
              style={{ fontFamily: MONO, fontSize: 12, fill: "var(--color-gold, #F59E0B)", letterSpacing: 1.5 }}
            >
              ROUND {state.round}
            </text>
            <text
              x={230}
              y={140}
              textAnchor="middle"
              style={{ fontFamily: MONO, fontSize: 7, fill: "#64748B", letterSpacing: 2 }}
            >
              OF {ROUNDS} · STRUCTURED DEBATE
            </text>

            {/* speaking ripples (imperative — painted in onFrame) */}
            {[0, 1].map((k) => (
              <circle
                key={k}
                ref={(el) => {
                  rippleRefs.current[k] = el;
                }}
                r={12}
                fill="none"
                stroke="var(--color-gold, #F59E0B)"
                strokeWidth={1.1}
                opacity={0}
              />
            ))}

            {/* seats */}
            <g>
              {SEATS.map((seat, i) => {
                const speaking = state.speaking?.includes(i) ?? false;
                const v = 5 + (CONV_TARGET[i] - 5) * state.convFraction;
                const dashOffset = RING_CIRC * (1 - v / 10);
                return (
                  <g key={seat.label}>
                    {/* conviction ring */}
                    <circle
                      cx={seat.x}
                      cy={seat.y}
                      r={RING_R}
                      fill="none"
                      strokeWidth={2.2}
                      strokeLinecap="round"
                      stroke={state.colorized ? convColor(CONV_TARGET[i]) : "rgba(148,163,184,.35)"}
                      strokeDasharray={RING_CIRC}
                      strokeDashoffset={dashOffset}
                      transform={`rotate(-90 ${seat.x} ${seat.y})`}
                      style={{ transition: `stroke-dashoffset .5s ${EASE}, stroke .5s ${EASE}` }}
                    />
                    {/* core */}
                    <circle
                      cx={seat.x}
                      cy={seat.y}
                      r={11.5}
                      fill={speaking ? "rgba(245,158,11,.25)" : "var(--color-surface, #1E293B)"}
                      stroke={speaking ? "var(--color-gold, #F59E0B)" : "rgba(148,163,184,.45)"}
                      strokeWidth={1}
                      style={{ transition: `fill .3s ${EASE}, stroke .3s ${EASE}` }}
                    />
                    <text
                      x={seat.x}
                      y={seat.y + 3}
                      textAnchor="middle"
                      style={{
                        fontFamily: MONO,
                        fontSize: 8.5,
                        letterSpacing: 0.4,
                        fill: speaking ? "var(--color-gold, #F59E0B)" : "var(--color-text, #F1F5F9)",
                        transition: `fill .3s ${EASE}`,
                      }}
                    >
                      {seat.init}
                    </text>
                    <text
                      x={seat.x + seat.lx}
                      y={seat.y + seat.ly}
                      textAnchor={seat.anchor}
                      style={{
                        fontFamily: MONO,
                        fontSize: 8,
                        letterSpacing: 0.7,
                        fill: "var(--color-muted, #94A3B8)",
                      }}
                    >
                      {seat.label}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* arc pulse dots (imperative — painted in onFrame) */}
            {[0, 1].map((k) => (
              <circle
                key={k}
                ref={(el) => {
                  pulseRefs.current[k] = el;
                }}
                r={3}
                fill="var(--color-gold, #F59E0B)"
                opacity={0}
                style={{ filter: "drop-shadow(0 0 5px #F59E0B)" }}
              />
            ))}
          </svg>

          {/* consensus card — overlays the table center at the verdict */}
          <div
            aria-hidden={!state.consensusShown}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: state.consensusShown
                ? "translate(-50%, -50%) scale(1)"
                : "translate(-50%, -50%) scale(.92)",
              width: "min(320px, 80%)",
              background: "rgba(15,23,41,.95)",
              border: "1px solid rgba(245,158,11,.55)",
              borderRadius: 12,
              padding: "16px 18px",
              opacity: state.consensusShown ? 1 : 0,
              pointerEvents: "none",
              transition: `opacity .6s ${EASE}, transform .6s ${EASE}`,
              boxShadow: "0 16px 50px -10px rgba(0,0,0,.7)",
              fontFamily: MONO,
            }}
          >
            <div style={{ fontSize: 10, letterSpacing: "0.14em", color: "var(--color-gold, #F59E0B)" }}>
              CONSENSUS · ROUND {ROUNDS}
            </div>
            <div
              style={{
                fontFamily: SERIF,
                fontSize: 20,
                color: "var(--color-text, #F1F5F9)",
                margin: "7px 0 4px",
              }}
            >
              {consensus.headline}
            </div>
            <div style={{ fontSize: 10, color: "#64748B", lineHeight: 1.6 }}>
              KEY DISAGREEMENT · {consensus.disagreement}
              <br />
              BIAS FLAGGED · {consensus.biasNote}
            </div>
            <div style={{ marginTop: 12 }}>
              {scenarios.map((row) => (
                <div
                  key={row.label}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "44px 1fr 34px",
                    gap: 10,
                    alignItems: "center",
                    fontSize: 10,
                    color: "var(--color-muted, #94A3B8)",
                    padding: "3px 0",
                  }}
                >
                  <span>{row.label}</span>
                  <span
                    style={{
                      height: 5,
                      background: "rgba(148,163,184,.12)",
                      borderRadius: 3,
                      overflow: "hidden",
                    }}
                  >
                    <span
                      style={{
                        display: "block",
                        height: "100%",
                        borderRadius: 3,
                        background: row.fill,
                        width: state.consensusShown ? `${row.pct}%` : 0,
                        transition: `width .9s ${EASE}`,
                      }}
                    />
                  </span>
                  <span style={{ textAlign: "right" }}>{row.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ===== side: sentiment gauge + transcript + bias monitor ===== */}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 10,
              letterSpacing: "0.12em",
              color: "var(--color-dim, #7E8FA6)",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <b style={{ color: "var(--color-green, #10B981)", fontWeight: 500 }}>
              BULL {state.sentimentBull}%
            </b>
            <span>PANEL SENTIMENT</span>
            <b style={{ color: "var(--color-red, #EF4444)", fontWeight: 500 }}>
              BEAR {100 - state.sentimentBull}%
            </b>
          </div>
          <div
            role="img"
            aria-label={`Panel sentiment: ${state.sentimentBull}% bull, ${100 - state.sentimentBull}% bear`}
            style={{
              display: "flex",
              height: 8,
              borderRadius: 5,
              overflow: "hidden",
              background: "rgba(148,163,184,.1)",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                background: "var(--color-green, #10B981)",
                width: `${state.sentimentBull}%`,
                transition: `width .55s ${EASE}`,
              }}
            />
            <div style={{ background: "var(--color-red, #EF4444)", flex: 1, transition: `width .55s ${EASE}` }} />
          </div>

          <div
            style={{
              fontFamily: MONO,
              fontSize: 10,
              color: "var(--color-dim, #7E8FA6)",
              letterSpacing: "0.12em",
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <span>DEBATE FEED</span>
            <b style={{ color: "var(--color-gold, #F59E0B)", fontWeight: 500 }}>
              ROUNDS BATCHED · 8 AGENTS / CALL
            </b>
          </div>

          {TRANSCRIPT.map((line, i) => {
            const shown = i < state.transcriptShown;
            const live = state.liveLine === i;
            const tag = PERSONA_STYLE[line.persona];
            return (
              <div
                key={line.tag}
                style={{
                  fontFamily: MONO,
                  fontSize: 11,
                  lineHeight: 1.5,
                  padding: "7px 10px",
                  marginBottom: 5,
                  borderLeft: `2px solid ${live ? "var(--color-gold, #F59E0B)" : "transparent"}`,
                  borderRadius: "0 6px 6px 0",
                  background: live ? "rgba(30,41,59,.6)" : "transparent",
                  color: live ? "var(--color-muted, #94A3B8)" : "var(--color-dim, #7E8FA6)",
                  opacity: shown ? (live ? 1 : 0.55) : 0,
                  transform: shown ? "none" : "translateY(6px)",
                  transition: `opacity .4s ${EASE}, transform .4s ${EASE}, background .4s ${EASE}`,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    fontSize: 9,
                    letterSpacing: "0.06em",
                    borderRadius: 4,
                    padding: "1px 6px",
                    marginRight: 8,
                    fontWeight: 500,
                    background: tag.bg,
                    color: tag.color,
                  }}
                >
                  {line.tag}
                </span>
                {line.text}
              </div>
            );
          })}

          <div
            style={{
              marginTop: 12,
              fontFamily: MONO,
              fontSize: 10,
              letterSpacing: "0.06em",
              color: "var(--color-dim, #7E8FA6)",
              border: `1px dashed ${LINE_STRONG}`,
              borderRadius: 8,
              padding: "8px 12px",
              opacity: state.biasShown ? 1 : 0,
              transform: state.biasShown ? "none" : "translateY(6px)",
              transition: `opacity .5s ${EASE}, transform .5s ${EASE}`,
            }}
          >
            <b style={{ color: "var(--color-gold, #F59E0B)", fontWeight: 500 }}>BIAS MONITOR</b> ·{" "}
            {biasMonitor}
          </div>
        </div>
      </div>

      {/* compliance framing — required copy */}
      <div
        style={{
          marginTop: 18,
          borderTop: `1px solid ${LINE}`,
          paddingTop: 12,
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: "0.06em",
          lineHeight: 1.7,
          color: "var(--color-dim, #7E8FA6)",
        }}
      >
        EXPERIMENTAL AI SIMULATION: the debate above is an illustrative re-enactment.
        Agents are AI personas that may voice strong opinions; WhatsTheMoat endorses
        none of them. Educational research, not investment advice.
      </div>
    </section>
  );
}
